import { useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import { fetchFileDetails } from "containers/file/file.Slice";
import { annulDocument } from "services/upload-file.service";
import { Button as PolarisButton, InlineStack } from "@shopify/polaris";
import { Button } from "catalyst-ui-kit/button";
import { Textarea } from "catalyst-ui-kit/textarea";
import { toast } from "sonner";

// Mirrors DocumentAnnulmentService's REASON_MAX_LENGTH — kept in sync manually
// since the frontend has no shared constants module with the backend.
const REASON_MAX_LENGTH = 2000;

/** Mirrors the backend's GET /:companyId/files/:id `annulEffects` field —
 * null/absent when the document is not annul-able at all. */
interface AnnulEffects {
  leaveRequest: boolean;
  businessTrip: boolean;
  tripReport: boolean;
}

interface AnnulDocumentConfirmProps {
  fileId: string;
  /** Lets the caller refresh anything that isn't redux-backed (e.g. DocumentAnnulmentPanel). */
  onDone?: () => void;
  /** Which linked records annulling this document will affect. Absent/null
   * on older responses is treated as "no linked effects" — those lines are
   * simply not shown, rather than assuming they apply. */
  annulEffects?: AnnulEffects | null;
}

const AnnulDocumentConfirm: React.FC<AnnulDocumentConfirmProps> = ({
  fileId,
  onDone,
  annulEffects,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams();

  const [reason, setReason] = useState<string>("");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const handleConfirm = async () => {
    if (
      !companyId ||
      !fileId ||
      !reason.trim() ||
      reason.trim().length > REASON_MAX_LENGTH ||
      isLoading
    )
      return;
    try {
      setIsLoading(true);
      setErrMsg(null);
      await annulDocument(companyId, fileId, reason.trim());
      toast.success(t("annul_document_success"));
      dispatch(fetchFileDetails({ fileId, companyId }));
      onDone?.();
      closeModal();
    } catch (e: any) {
      setErrMsg(e?.response?.data?.message || "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {errMsg && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
          {t(errMsg)}
        </div>
      )}
      <h1 style={{ textAlign: "center", marginBottom: "8px" }}>
        {t("annul_document_confirm_title")}
      </h1>
      <ul className="list-disc space-y-1 pl-5 text-sm text-zinc-700 dark:text-zinc-300">
        <li>{t("annul_document_effect_kept")}</li>
        <li>{t("annul_document_effect_notice")}</li>
        <li>{t("annul_document_effect_renamed")}</li>
        {annulEffects?.leaveRequest && (
          <li>{t("annul_document_effect_leave")}</li>
        )}
        {annulEffects?.businessTrip && (
          <li>{t("annul_document_effect_trip")}</li>
        )}
        {annulEffects?.tripReport && (
          <li>{t("annul_document_effect_trip_report")}</li>
        )}
      </ul>
      <div>
        <label
          htmlFor="annul-document-reason"
          className="mb-1 block text-sm font-medium text-zinc-950 dark:text-white"
        >
          {t("annul_document_reason_label")} *
        </label>
        <Textarea
          id="annul-document-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t("annul_document_reason_placeholder")}
          rows={3}
          maxLength={REASON_MAX_LENGTH}
        />
        <p className="mt-1 text-right text-xs text-zinc-500">
          {reason.length}/{REASON_MAX_LENGTH}
        </p>
      </div>
      <InlineStack align="space-between">
        <PolarisButton
          onClick={handleConfirm}
          loading={isLoading}
          disabled={!reason.trim() || reason.trim().length > REASON_MAX_LENGTH}
          tone="critical"
          variant="primary"
        >
          {t("Yes")}
        </PolarisButton>
        <Button plain onClick={closeModal} disabled={isLoading}>
          {t("No")}
        </Button>
      </InlineStack>
    </div>
  );
};

export default AnnulDocumentConfirm;
